/**
 * Errores del cliente HTTP.
 *
 * Nada muere en silencio tampoco en la UI: cada falla llega tipada hasta la
 * pantalla, con un mensaje en espanol que le sirve a quien esta diagnosticando.
 */
import type { ZodIssue } from 'zod';

/** Error devuelto por la API con un codigo HTTP. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | null;
  readonly correlationId: string | null;

  constructor(
    status: number,
    message: string,
    detail: string | null = null,
    correlationId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.correlationId = correlationId;
  }
}

/**
 * Un error que repetir la misma consulta no arregla: el recurso no existe, no
 * hay permiso, la consulta esta mal armada. Es todo 4xx salvo 408 (timeout) y
 * 429 (limite de solicitudes), que si se resuelven esperando. La red caida y los
 * 5xx tampoco son permanentes: se arreglan solos, y reintentar es lo correcto.
 */
export function isPermanentApiError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

/** 401: el ID token vencio o no es valido. */
export class UnauthorizedError extends ApiError {
  constructor(detail: string | null = null) {
    super(401, 'Sesión no válida o expirada', detail);
    this.name = 'UnauthorizedError';
  }
}

/** 403: el usuario esta autenticado pero su rol no alcanza. */
export class ForbiddenError extends ApiError {
  constructor(detail: string | null = null) {
    super(403, 'Permisos insuficientes', detail);
    this.name = 'ForbiddenError';
  }
}

/**
 * La API respondio 2xx pero el cuerpo no calza con el contrato.
 * Es un error de integracion, no del usuario: se muestra con los issues de Zod
 * para que se pueda arreglar el contrato o el backend.
 */
export class ContractValidationError extends Error {
  readonly issues: readonly ZodIssue[];
  readonly path: string;

  constructor(path: string, issues: readonly ZodIssue[]) {
    super(`La respuesta de ${path} no cumple el contrato`);
    this.name = 'ContractValidationError';
    this.path = path;
    this.issues = issues;
  }
}

/** No hubo respuesta: la API esta caida, sin red o el proxy no responde. */
export class NetworkError extends Error {
  // `override` es obligatorio: `Error.cause` ya existe en la lib de ES2022 y
  // TypeScript exige declarar explicitamente que lo estamos sobreescribiendo.
  override readonly cause: unknown;

  constructor(path: string, cause: unknown) {
    super(`No se pudo contactar la API (${path})`);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Mensaje en espanol listo para mostrar en un banner de error. */
export function describeError(error: unknown): string {
  if (error instanceof UnauthorizedError) {
    return 'Tu sesión expiró. Vuelve a iniciar sesión con Google.';
  }
  if (error instanceof ForbiddenError) {
    // El 403 no siempre es el rol: la simulacion del picking lo usa cuando el
    // ambiente apunta a Janis produccion, y eso hay que leerlo tal cual.
    return error.detail
      ? `No tienes permisos para realizar esta acción: ${error.detail}`
      : 'No tienes permisos para realizar esta acción.';
  }
  if (error instanceof ContractValidationError) {
    return `${error.message}. Es un desajuste entre la API y @amoble/contracts.`;
  }
  if (error instanceof NetworkError) {
    return 'No se pudo contactar la API del middleware. Revisa que esté arriba.';
  }
  if (error instanceof ApiError) {
    return error.detail ? `${error.message}: ${error.detail}` : error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Error desconocido.';
}
